import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Download, Trash2, Phone, RefreshCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GlassCard } from "../_shared";

const ORDER_STATUSES = ["new", "in_progress", "completed", "cancelled"];
const CONTACT_STATUSES = ["new", "replied", "closed"];

function StatusBadge({ status }) {
  const map = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    in_progress: "bg-amber-100 text-amber-700 border-amber-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
    replied: "bg-emerald-100 text-emerald-700 border-emerald-200",
    closed: "bg-slate-200 text-slate-700 border-slate-300",
  };
  return <Badge className={`${map[status] || "bg-slate-100 text-slate-700"} border font-medium capitalize`}>{status.replace("_", " ")}</Badge>;
}

function toCsv(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map(r => keys.map(k => esc(r[k])).join(","))].join("\n");
}

function downloadCsv(name, rows) {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Info({ label, value, className = "" }) {
  return (
    <div className={`bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 ${className}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-900 mt-0.5 break-words text-sm">{value}</div>
    </div>
  );
}

export default function LeadsManager() {
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [activeType, setActiveType] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([api.get("/admin/orders"), api.get("/admin/contacts")]);
      setOrders(o.data); setContacts(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (filter !== "all") list = list.filter(o => o.status === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.name || "").toLowerCase().includes(q) ||
        (o.phone || "").includes(q) ||
        (o.vehicle_number || "").toLowerCase().includes(q) ||
        (o.district || "").toLowerCase().includes(q) ||
        (o.service_type || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, search, filter]);

  const filteredContacts = useMemo(() => {
    let list = contacts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.name || "").toLowerCase().includes(q) ||
        (o.phone || "").includes(q) ||
        (o.subject || "").toLowerCase().includes(q) ||
        (o.message || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [contacts, search]);

  const updateOrderStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, { status });
      setOrders((os) => os.map(o => o.id === id ? data : o));
      if (active && active.id === id) setActive(data);
      toast.success(`Order marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const updateContactStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/contacts/${id}`, { status });
      setContacts((cs) => cs.map(c => c.id === id ? data : c));
      if (active && active.id === id) setActive(data);
      toast.success(`Message marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order permanently?")) return;
    try { await api.delete(`/admin/orders/${id}`); setOrders(os => os.filter(o => o.id !== id)); setActive(null); toast.success("Deleted"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try { await api.delete(`/admin/contacts/${id}`); setContacts(cs => cs.filter(c => c.id !== id)); setActive(null); toast.success("Deleted"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader
        title="Leads Manager"
        subtitle="All FASTag orders and contact-form submissions in one place. Filter, search and export."
        actions={
          <>
            <Button variant="outline" onClick={load} className="rounded-full"><RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh</Button>
            <Button onClick={() => downloadCsv("orders.csv", filteredOrders)} className="bg-[#0E7490] hover:bg-[#0F5F72] text-white rounded-full" data-testid="export-orders-csv"><Download className="w-4 h-4 mr-1.5" /> Export Orders</Button>
          </>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, vehicle, district…" className="pl-9" data-testid="leads-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="sm:w-56" data-testid="leads-filter"><SelectValue placeholder="Status filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {ORDER_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-full p-1">
          <TabsTrigger value="orders" data-testid="admin-tab-orders" className="rounded-full data-[state=active]:bg-[#0057B8] data-[state=active]:text-white px-6">Orders ({filteredOrders.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-full data-[state=active]:bg-[#0057B8] data-[state=active]:text-white px-6">Messages ({filteredContacts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <GlassCard>
            <Table data-testid="admin-orders-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-14 text-slate-500">No orders match your filters.</TableCell></TableRow>
                )}
                {filteredOrders.map(o => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-blue-50" onClick={() => { setActive(o); setActiveType("order"); }}>
                    <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString()}<div className="text-[10px] text-slate-500">{new Date(o.created_at).toLocaleTimeString()}</div></TableCell>
                    <TableCell className="font-medium">{o.name}</TableCell>
                    <TableCell><a href={`tel:${o.phone}`} className="text-[#0057B8] hover:underline" onClick={(e) => e.stopPropagation()}>{o.phone}</a></TableCell>
                    <TableCell>{o.service_type}</TableCell>
                    <TableCell><div className="font-mono text-xs">{o.vehicle_number}</div><div className="text-xs text-slate-500">{o.vehicle_type}</div></TableCell>
                    <TableCell>{o.district}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GlassCard>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <GlassCard>
            <Table data-testid="admin-contacts-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-14 text-slate-500">No messages match your filters.</TableCell></TableRow>
                )}
                {filteredContacts.map(c => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-blue-50" onClick={() => { setActive(c); setActiveType("contact"); }}>
                    <TableCell className="text-xs">{new Date(c.created_at).toLocaleDateString()}<div className="text-[10px] text-slate-500">{new Date(c.created_at).toLocaleTimeString()}</div></TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><a href={`tel:${c.phone}`} className="text-[#0057B8] hover:underline" onClick={(e) => e.stopPropagation()}>{c.phone}</a></TableCell>
                    <TableCell>{c.subject || "—"}</TableCell>
                    <TableCell className="max-w-xs truncate">{c.message}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GlassCard>
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-2xl bg-white">
          {active && activeType === "order" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">Order — {active.name} <StatusBadge status={active.status} /></DialogTitle>
                <DialogDescription>Ref {active.id}</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Info label="Phone" value={<a href={`tel:${active.phone}`} className="text-[#0057B8] hover:underline">{active.phone}</a>} />
                <Info label="WhatsApp" value={active.whatsapp || "—"} />
                <Info label="Email" value={active.email || "—"} />
                <Info label="Service" value={active.service_type} />
                <Info label="Vehicle" value={`${active.vehicle_number} (${active.vehicle_type})`} />
                <Info label="RC/Chassis" value={active.vehicle_rc || "—"} />
                <Info label="District" value={active.district} />
                <Info label="Pincode" value={active.pincode} />
                <Info label="Address" value={active.address} className="sm:col-span-2" />
                <Info label="Notes" value={active.notes || "—"} className="sm:col-span-2" />
                <Info label="Created" value={new Date(active.created_at).toLocaleString()} className="sm:col-span-2" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                <div className="flex-1">
                  <Select value={active.status} onValueChange={(v) => updateOrderStatus(active.id, v)}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <a href={`https://wa.me/${active.whatsapp || active.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:bg-[#128C7E]"><Phone className="w-4 h-4" /> WhatsApp</a>
                <Button variant="destructive" onClick={() => deleteOrder(active.id)} className="rounded-full"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
              </div>
            </>
          )}
          {active && activeType === "contact" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">Message — {active.name} <StatusBadge status={active.status} /></DialogTitle>
                <DialogDescription>Ref {active.id}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Info label="Phone" value={<a href={`tel:${active.phone}`} className="text-[#0057B8] hover:underline">{active.phone}</a>} />
                <Info label="Email" value={active.email || "—"} />
                <Info label="Subject" value={active.subject || "—"} />
                <Info label="Message" value={<div className="whitespace-pre-wrap">{active.message}</div>} />
                <Info label="Created" value={new Date(active.created_at).toLocaleString()} />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                <div className="flex-1">
                  <Select value={active.status} onValueChange={(v) => updateContactStatus(active.id, v)}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTACT_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="destructive" onClick={() => deleteContact(active.id)} className="rounded-full"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
